/**
 * @tidings/browser — the hosted snippet served from
 * https://tidingshq.com/js/v1/tidings.js.
 *
 * One script tag, no build step, no runtime dependencies. It mints a
 * persistent anonymous id, sends page views (including SPA route changes),
 * batches everything through the public ingestion endpoint with `keepalive`
 * so the last batch survives the unload, and links the anonymous half of a
 * visit to the account on `identify()` via POST /api/v1/alias/.
 *
 * With `heatmaps` on it also records `$click` (position + a short element
 * label) and one `$scroll` per view (depth + dwell). Both are off by default,
 * both are suppressed under Global Privacy Control, and neither ever records
 * an input value. A page view sent under GPC carries `gpc: true` so the
 * heatmap view can explain the clicks that will never arrive.
 *
 * Two rules the whole file obeys: never throw into the host page (every
 * public entry point is wrapped), and write nothing to the browser's storage
 * before `init()` has been given a key and consent allows it.
 */

type Props = Record<string, unknown>;

export type QueuedEvent = {
  name: string;
  distinct_id: string;
  timestamp: string;
  properties: Props;
};

export type InitOptions = {
  /** Project ingestion key (`td_…`). Without it every method no-ops. */
  key?: string;
  /** API origin. Defaults to Tidings Cloud. */
  apiUrl?: string;
  /** Send `page_view` on load and on SPA route changes. Default true. */
  autoPageviews?: boolean;
  /** Record `$click` and `$scroll`. Default false. */
  heatmaps?: boolean;
  /** Capture `txt`/`aria` labels with clicks. Default true. */
  heatmapsText?: boolean;
  /** Cookie `Domain=` for the anonymous id, e.g. `.example.com`. */
  cookieDomain?: string;
  /** `required` holds everything back until `consent(true)`. */
  consent?: "notrequired" | "required" | "granted";
  /** Inspect or rewrite every event; return null to drop it. JS init only. */
  beforeSend?: (event: QueuedEvent) => QueuedEvent | null | void;
};

const ANON_STORAGE_KEY = "tidings_anon_id";
const ANON_TS_KEY = "tidings_anon_id_ts";
const ID_STORAGE_KEY = "tidings_distinct_id";
const ALIAS_STORAGE_KEY = "tidings_aliased";
const DEFAULT_API_URL = "https://api.tidingshq.com";
const OVERLAY_URL = "https://tidingshq.com/js/v1/tidings-overlay.js";
const OVERLAY_PARAM = "tidings_heatmap";
const BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 2000;
/** 13 months: the longest an analytics id should outlive its visitor. */
const COOKIE_MAX_AGE = 60 * 60 * 24 * 396;
const ID_TTL_MS = COOKIE_MAX_AGE * 1000;
const DWELL_CAP_MS = 6 * 60 * 60 * 1000;
const CLICK_CAP = 50;
/** How many anon→user pairs we remember, so a reload never re-posts an alias. */
const ALIAS_MEMORY = 5;
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];
/** An email, or a run of digits long enough to be an id, phone or card. */
const SENSITIVE = /[\w.+-]+@[\w-]+\.\w{2,}|\d{6,}/;

let apiKey = "";
let apiUrl = DEFAULT_API_URL;
let started = false;
let historyPatched = false;
let lastPath: string | null = null;
let autoPageviews = true;
/** Last resort when both localStorage and cookies are unavailable. */
let memoryAnonId: string | null = null;
let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
/** Events that already failed once; a second failure drops them. */
const retried = new WeakSet<QueuedEvent>();

// consent / privacy
let granted = true;
let gpc = false;
let cookieDomain = "";
let beforeSend: InitOptions["beforeSend"];

// heatmaps
let heatmaps = false;
let heatmapsText = true;
/** True while the read-only overlay owns this view: record nothing. */
let suspended = false;
let viewPath = "";
let viewStart = 0;
let maxY = 0;
let docH = 0;
let clickCount = 0;
let scrollSent = false;

// --- storage -------------------------------------------------------------
// Every accessor can throw (Safari private mode, blocked third-party
// storage), so all of them are total functions that fall back to null.

function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // storage unavailable
  }
}

function removeLocal(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // storage unavailable
  }
}

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name + "=([^;]*)"),
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string, maxAge: number): void {
  try {
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; Max-Age=" +
      maxAge +
      "; Path=/; SameSite=Lax" +
      (cookieDomain ? "; Domain=" + cookieDomain : "") +
      (location.protocol === "https:" ? "; Secure" : "");
  } catch {
    // cookies blocked
  }
}

// --- ids -----------------------------------------------------------------

function uuid(): string {
  // Web Crypto where it exists — `randomUUID` needs a secure context, so a
  // plain-http page falls back to getRandomValues and only then to Math.random
  // (one nibble per template character either way).
  let rand = (): number => (Math.random() * 16) | 0;
  try {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    let i = 0;
    rand = () => bytes[i++] & 15;
  } catch {
    // no Web Crypto at all
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = rand();
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * Persist the id — unless Global Privacy Control is on, or consent has not
 * been given, in which case it lives in memory for this page only.
 */
function storeAnonId(id: string): void {
  memoryAnonId = id;
  if (gpc || !granted) return;
  writeLocal(ANON_STORAGE_KEY, id);
  writeLocal(ANON_TS_KEY, String(Date.now()));
  writeCookie(ANON_STORAGE_KEY, id, COOKIE_MAX_AGE);
}

/**
 * localStorage, then a 13-month cookie, then memory — in that order. An id
 * whose first-seen stamp is older than 13 months is retired and replaced; one
 * stored by an older build carries no stamp, so it gets today's.
 */
function anonId(): string {
  let stored = memoryAnonId;
  if (!gpc) {
    stored =
      readLocal(ANON_STORAGE_KEY) || readCookie(ANON_STORAGE_KEY) || stored;
    if (stored) {
      const born = +(readLocal(ANON_TS_KEY) || 0);
      if (born && Date.now() - born > ID_TTL_MS) stored = null;
      else if (!born) writeLocal(ANON_TS_KEY, String(Date.now()));
    }
  }
  if (stored) {
    // Mirror into whichever layer was missing it, so a cookie-only visitor
    // who later gains localStorage keeps the same id.
    memoryAnonId = stored;
    return stored;
  }
  const minted = "anon_" + uuid();
  storeAnonId(minted);
  return minted;
}

// --- transport -----------------------------------------------------------

function post(path: string, body: unknown): Promise<Response> {
  return fetch(apiUrl + path, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-API-Key": apiKey },
    body: JSON.stringify(body),
    // Header auth rules out sendBeacon (it cannot set headers), so keepalive
    // is what keeps the final batch alive through the unload.
    keepalive: true,
  });
}

function requeue(events: QueuedEvent[]): void {
  const fresh = events.filter((event) => !retried.has(event));
  if (!fresh.length) return; // second failure: drop rather than loop
  for (const event of fresh) retried.add(event);
  queue = fresh.concat(queue);
  schedule();
}

function send(events: QueuedEvent[]): void {
  try {
    void post("/api/v1/events/", { events })
      .then((response) => {
        // 4xx is our own bug (bad key, bad payload); retrying cannot fix it,
        // but a 429 or a 5xx is worth exactly one more attempt. Keeping one
        // rule for both is simpler than a status table and costs one request.
        if (!response || !response.ok) requeue(events);
      })
      .catch(() => requeue(events));
  } catch {
    requeue(events);
  }
}

function flush(): void {
  try {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (!queue.length) return;
    const events = queue;
    queue = [];
    send(events);
  } catch {
    // never throw into a page lifecycle handler
  }
}

function schedule(): void {
  if (timer !== null) return;
  timer = setTimeout(flush, FLUSH_DELAY_MS);
}

function enqueue(name: string, properties?: Props): void {
  if (!granted) return; // consent required and not yet given: drop, don't hold
  const base: QueuedEvent = {
    name,
    distinct_id: getDistinctId(),
    timestamp: new Date().toISOString(),
    properties: properties || {},
  };
  let event: QueuedEvent | null = base;
  if (beforeSend) {
    try {
      const returned = beforeSend(base);
      event = returned === undefined ? base : returned;
    } catch {
      event = base; // a broken hook must not lose the event
    }
  }
  if (!event) return;
  queue.push(event);
  if (queue.length >= BATCH_SIZE) flush();
  else schedule();
}

// --- page views ----------------------------------------------------------

function pageProperties(): Props {
  const properties: Props = {
    path: location.pathname,
    host: location.hostname,
    title: document.title,
  };
  // The one place GPC is visible downstream. A browser sending it records no
  // clicks, so a heatmap of that traffic is empty by design — and an operator
  // testing their own install in Brave has no way to tell that from a broken
  // snippet unless the page views say so.
  if (gpc) properties.gpc = true;
  if (document.referrer) properties.referrer = document.referrer;
  try {
    const params = new URLSearchParams(location.search);
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) properties[key] = value;
    }
  } catch {
    // no URLSearchParams: skip campaign attribution rather than fail
  }
  return properties;
}

function sendPageView(): void {
  try {
    const path = location.pathname;
    // A framework that replaces state on every render would otherwise inflate
    // the page-view count; only a path change counts.
    if (path === lastPath) return;
    lastPath = path;
    startView();
    enqueue("page_view", pageProperties());
  } catch {
    // never throw from a patched history method
  }
}

function patchHistory(): void {
  if (historyPatched) return;
  historyPatched = true;
  try {
    const history = window.history;
    const methods = ["pushState", "replaceState"] as const;
    for (const method of methods) {
      const original = history[method];
      if (typeof original !== "function") continue;
      history[method] = function patched(
        this: History,
        ...args: Parameters<History["pushState"]>
      ): void {
        original.apply(this, args);
        sendPageView();
      };
    }
    window.addEventListener("popstate", sendPageView);
  } catch {
    // history unavailable: the initial page view still went out
  }
}

// --- heatmaps ------------------------------------------------------------

/** Snapshot the scroll extent now: Astro swaps the DOM before `pushState`. */
function snapshot(): void {
  try {
    maxY = Math.max(maxY, (window.scrollY || 0) + window.innerHeight);
    docH = document.documentElement.scrollHeight || docH;
  } catch {
    // layout unavailable
  }
}

/** One `$scroll` per view, emitted when the view ends rather than as it goes. */
function finishScroll(): void {
  try {
    if (!heatmaps || suspended || scrollSent || !viewStart) return;
    scrollSent = true;
    enqueue("$scroll", {
      path: viewPath,
      host: location.hostname,
      depth: Math.round(Math.min(100, (maxY / (docH || 1)) * 100)) / 100,
      vw: window.innerWidth,
      vh: window.innerHeight,
      dh: docH,
      dwell_ms: Math.min(Date.now() - viewStart, DWELL_CAP_MS),
    });
  } catch {
    // never throw from a lifecycle handler
  }
}

/** Close the previous view's scroll, then reset the per-view counters. */
function startView(): void {
  if (!heatmaps) return;
  finishScroll();
  // The overlay suspends only the view it was opened on; the first view here
  // *is* that view (viewStart is still 0), so it keeps the flag.
  if (viewStart) suspended = false;
  viewPath = location.pathname;
  viewStart = Date.now();
  clickCount = 0;
  scrollSent = false;
  maxY = 0;
  docH = 0;
  snapshot();
}

/**
 * The element and up to three ancestors (stopping before `body`), each as
 * `tag#id` or `tag` plus at most two hand-written class names. Classes with
 * digits or great length are hashed/utility names: no signal, and they change
 * on the next build anyway.
 */
function selector(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  for (let i = 0; node && node.tagName !== "BODY" && i < 4; i++) {
    const tag = node.tagName.toLowerCase();
    let part = tag;
    if (node.id) {
      part += "#" + node.id;
    } else {
      const kept: string[] = [];
      for (const name of (node.getAttribute("class") || "").split(/\s+/)) {
        if (!name || name.length > 24 || /\d/.test(name)) continue;
        kept.push(name);
        if (kept.length === 2) break;
      }
      if (kept.length) part += "." + kept.join(".");
    }
    parts.unshift(part);
    node = node.parentElement;
  }
  return parts.join(">").slice(0, 160);
}

/** Collapse to one line, drop anything that looks like personal data. */
function label(text: string | null | undefined): string | undefined {
  if (!text) return;
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (!collapsed || SENSITIVE.test(collapsed)) return;
  return collapsed.slice(0, 40);
}

function onClick(event: MouseEvent): void {
  try {
    if (suspended || event.button !== 0 || clickCount >= CLICK_CAP) return;
    const el = event.target;
    if (!(el instanceof Element)) return;
    const tag = el.tagName.toLowerCase();
    if (tag === "input" && (el as HTMLInputElement).type === "password") return;
    clickCount++;
    const root = document.documentElement;
    const props: Props = {
      path: location.pathname,
      host: location.hostname,
      x: Math.round((event.pageX / (root.scrollWidth || 1)) * 1000) / 1000,
      y: Math.round(event.pageY),
      vw: window.innerWidth,
      // The exact denominator of `x`, so the server can re-anchor the click
      // into whatever box the heatmap is drawn on. `vw` is close but not it.
      dw: root.scrollWidth,
      vh: window.innerHeight,
      dh: root.scrollHeight,
      tag,
      sel: selector(el),
    };
    if (heatmapsText && !el.closest("[data-tidings-mask]")) {
      if (
        !/^(input|textarea|select)$/.test(tag) &&
        !el.closest("[contenteditable]")
      ) {
        const text = label((el as HTMLElement).innerText || el.textContent);
        if (text) props.txt = text;
      }
      const aria = label(el.getAttribute("aria-label"));
      if (aria) props.aria = aria;
    }
    const role = el.getAttribute("role");
    if (role) props.role = role;
    const anchor = el.closest("a");
    if (anchor && anchor.getAttribute("href")) {
      props.href = (anchor.host + anchor.pathname).slice(0, 120);
    }
    enqueue("$click", props);
  } catch {
    // a click handler that throws would surface in the host page
  }
}

/**
 * `?tidings_heatmap=<token>` means someone opened this page from the Tidings
 * heatmap view. Strip the param (so it never reaches an analytics property or
 * a share), stop recording for this view, and pull in the overlay renderer.
 */
function overlayHook(): boolean {
  try {
    const params = new URLSearchParams(location.search);
    const token = params.get(OVERLAY_PARAM);
    if (!token) return false;
    params.delete(OVERLAY_PARAM);
    const query = params.toString();
    history.replaceState(
      history.state,
      "",
      location.pathname + (query ? "?" + query : "") + location.hash,
    );
    const script = document.createElement("script");
    script.src = OVERLAY_URL;
    script.dataset.token = token;
    script.dataset.apiUrl = apiUrl;
    // A 404 here is a no-op: nothing else depends on the overlay loading.
    document.head.appendChild(script);
    return true;
  } catch {
    return false;
  }
}

// --- public API ----------------------------------------------------------

/** Start the client. Idempotent — a second call is ignored. */
export function init(options: InitOptions = {}): void {
  try {
    if (started || typeof window === "undefined") return;
    started = true;
    apiKey = options.key || "";
    apiUrl = (options.apiUrl || DEFAULT_API_URL).replace(/\/+$/, "");
    cookieDomain = options.cookieDomain || "";
    beforeSend = options.beforeSend;
    granted = options.consent !== "required";
    autoPageviews = options.autoPageviews !== false;
    gpc = navigator.globalPrivacyControl === true;
    // Under GPC the visit still counts, but nothing about it is remembered
    // between pages and nothing about the pointer is recorded.
    heatmaps = options.heatmaps === true && !gpc;
    heatmapsText = options.heatmapsText !== false;
    if (!apiKey) return;
    // pagehide fires where unload does not (bfcache, iOS); visibilitychange
    // covers the tab switch that precedes most closes. The scroll event is
    // queued before the flush, so ordering never depends on registration.
    const onHide = (): void => {
      finishScroll();
      flush();
    };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    if (heatmaps) {
      window.addEventListener("scroll", snapshot, { passive: true });
      window.addEventListener("resize", snapshot, { passive: true });
      document.addEventListener("click", onClick, true);
    }
    suspended = overlayHook();
    if (autoPageviews) {
      patchHistory();
      sendPageView();
    } else {
      startView();
    }
  } catch {
    // a broken init must not take the host page with it
  }
}

/** Queue an event. Flushed at 20 events, after 2s, or on page hide. */
export function track(name: string, properties?: Props): void {
  try {
    if (!apiKey || !name) return;
    enqueue(name, properties);
  } catch {
    // analytics must never break the caller
  }
}

/**
 * Tie this browser to an account id. The first time an anonymous id is
 * linked to a user we POST /api/v1/alias/, which rewrites the anonymous
 * events onto the account — so the visit that led to the signup keeps its
 * history. Fire-and-forget: a 409 (the id is already claimed) is not the
 * caller's problem.
 */
export function identify(userId: string, traits?: Props): void {
  try {
    if (!apiKey || !userId || !granted) return;
    const current = getDistinctId();
    if (current !== userId) {
      const pair = current + ">" + userId;
      const seen = (readLocal(ALIAS_STORAGE_KEY) || "").split("\n");
      if (seen.indexOf(pair) < 0) {
        writeLocal(
          ALIAS_STORAGE_KEY,
          seen
            .filter(Boolean)
            .concat(pair)
            .slice(-ALIAS_MEMORY)
            .join("\n"),
        );
        void post("/api/v1/alias/", {
          distinct_id: userId,
          anonymous_id: current,
        }).catch(() => {
          // The alias is best-effort; the events still land under the
          // account from here on.
        });
      }
      writeLocal(ID_STORAGE_KEY, userId);
    }
    if (traits) track("$identify", { $set: traits });
  } catch {
    // never break a sign-in
  }
}

/**
 * Record a consent decision from the site's own banner. `consent(true)` on a
 * client started with `consent: "required"` releases storage and sends the
 * page view that was held back; `consent(false)` erases the stored id and
 * stops, whatever the mode.
 */
export function consent(allowed: boolean): void {
  try {
    if (allowed) {
      if (granted) return;
      granted = true;
      if (autoPageviews) {
        lastPath = null;
        sendPageView();
      }
    } else {
      granted = false;
      queue = [];
      memoryAnonId = null;
      removeLocal(ANON_STORAGE_KEY);
      removeLocal(ANON_TS_KEY);
      removeLocal(ID_STORAGE_KEY);
      removeLocal(ALIAS_STORAGE_KEY);
      writeCookie(ANON_STORAGE_KEY, "", 0);
    }
  } catch {
    // a consent banner must never see an exception from us
  }
}

/** Forget the account and mint a fresh anonymous id. Call it on sign-out. */
export function reset(): void {
  try {
    removeLocal(ID_STORAGE_KEY);
    removeLocal(ALIAS_STORAGE_KEY);
    memoryAnonId = null;
    storeAnonId("anon_" + uuid());
  } catch {
    // storage unavailable
  }
}

/** The id events are currently attributed to. */
export function getDistinctId(): string {
  try {
    if (!apiKey || !granted) return "";
    return readLocal(ID_STORAGE_KEY) || anonId();
  } catch {
    return "";
  }
}

const api = { init, track, identify, consent, reset, getDistinctId };

declare global {
  interface Window {
    tidings?: typeof api;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

try {
  if (typeof window !== "undefined") {
    window.tidings = api;
    // <script defer src=".../tidings.js" data-key="td_…"> self-starts.
    const script = document.currentScript as HTMLScriptElement | null;
    const data = script && script.dataset;
    if (data && data.key) {
      init({
        key: data.key,
        apiUrl: data.apiUrl,
        autoPageviews: data.autoPageviews !== "false",
        heatmaps: data.heatmaps === "true",
        heatmapsText: data.heatmapsText !== "off",
        cookieDomain: data.cookieDomain,
        consent: data.consent as InitOptions["consent"],
      });
    }
  }
} catch {
  // a hostile or exotic host page must not surface an error from us
}

export default api;
